import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { useAppSettings, resolveImageUrl } from "../context/AppSettingsContext";
import BranchMultiSelect from "../components/BranchMultiSelect";
import {
  Cake, Gift, RefreshCw, Search, Send, Sparkles, Award, Wallet,
  HeartHandshake, UserMinus, Settings, TrendingUp,
} from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Cell } from "recharts";

const KINDS = [
  { k: "all",         label: "Tutti",         icon: Sparkles },
  { k: "birthday",    label: "Compleanni",    icon: Cake },
  { k: "anniversary", label: "Anniversari",   icon: Gift },
  { k: "winback",     label: "Recupero",      icon: HeartHandshake },
];

const KIND_META = {
  birthday:    { label: "🎂 Compleanno",   pill: "pill-red",  color: "#f43f5e" },
  anniversary: { label: "🎁 Anniversario", pill: "pill-mint", color: "#10b981" },
  winback:     { label: "💌 Recupero",     pill: "pill-gold", color: "#d946ef" },
};

export default function Birthdays() {
  const [kind, setKind] = useState("all");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [runningBday, setRunningBday] = useState(false);
  const [runningAniv, setRunningAniv] = useState(false);
  const [runningWback, setRunningWback] = useState(false);
  const [reissuingId, setReissuingId] = useState(null);
  const [previewRow, setPreviewRow] = useState(null);
  const [statsDays, setStatsDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [statsView, setStatsView] = useState("kind"); // "kind" | "branch"
  const [statsBranchRows, setStatsBranchRows] = useState([]);
  const [cfgOpen, setCfgOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = kind === "all" ? {} : { kind };
      const { data } = await api.get("/coupons/auto/log", { params });
      setRows(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const loadStats = async () => {
    try {
      const [byKind, byBranch] = await Promise.all([
        api.get("/coupons/auto/stats", { params: { days: statsDays } }).then(r => r.data),
        api.get("/coupons/auto/stats/by-branch", { params: { days: statsDays } }).then(r => r.data).catch(() => []),
      ]);
      setStats(byKind);
      setStatsBranchRows(byBranch || []);
    } catch { setStats(null); setStatsBranchRows([]); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);
  useEffect(() => { loadStats(); /* eslint-disable-next-line */ }, [statsDays]);

  const runNow = async (which) => {
    const setterMap = { birthday: setRunningBday, anniversary: setRunningAniv, winback: setRunningWback };
    const labelMap = { birthday: "Compleanni", anniversary: "Anniversari", winback: "Recupero" };
    setterMap[which](true);
    try {
      const { data } = await api.post(`/segments/${which}/run-now`);
      const suffix = which === "winback" ? ` · ${data.skipped_recent || 0} già coperti` : "";
      toast.success(`${labelMap[which]}: ${data.coupons_created} creati · ${data.coupons_reused} riusati${suffix}`);
      load(); loadStats();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setterMap[which](false); }
  };

  const reissue = async (row) => {
    const cust = row.customer;
    if (!cust) return;
    const kLabel = KIND_META[row.kind]?.label || row.kind;
    if (!window.confirm(`Riemettere il coupon ${kLabel} per ${cust.name}?`)) return;
    setReissuingId(cust.id + row.kind);
    try {
      const { data } = await api.post(`/customers/${cust.id}/auto-coupon/reissue`, { kind: row.kind });
      toast.success(data.created ? `Nuovo coupon ${data.coupon.code} generato` : `Coupon ${data.coupon.code} già esistente, verificato`);
      load(); loadStats();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setReissuingId(null); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) =>
      (r.customer?.name || "").toLowerCase().includes(s) ||
      (r.customer?.card_number || "").toLowerCase().includes(s) ||
      (r.code || "").toLowerCase().includes(s)
    );
  }, [rows, q]);

  const rowStats = useMemo(() => ({
    total: rows.length,
    bday: rows.filter((r) => r.kind === "birthday").length,
    aniv: rows.filter((r) => r.kind === "anniversary").length,
    wback: rows.filter((r) => r.kind === "winback").length,
    used: rows.filter((r) => r.status === "used").length,
  }), [rows]);

  const chartData = useMemo(() => {
    if (!stats) return [];
    return ["birthday", "anniversary", "winback"].map((k) => ({
      key: k,
      name: KIND_META[k].label.replace(/^\S+ /, ""),
      color: KIND_META[k].color,
      issued: stats[k]?.issued || 0,
      used: stats[k]?.used || 0,
      rate: Math.round(((stats[k]?.rate || 0) * 100)),
    }));
  }, [stats]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="birthdays-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-gold"><Cake className="h-3 w-3" /> COUPON AUTOMATICI</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">Compleanni, Anniversari & Recupero</h1>
          <p className="text-zinc-400 mt-1">Regali automatici per fidelizzare · importo scalato per anzianità.</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-end flex-wrap">
          <Button data-testid="birthdays-config-open" onClick={() => setCfgOpen(true)} variant="outline"
                  className="border-zinc-800 hover:border-amber-500/60 h-11">
            <Settings className="h-4 w-4 mr-2" /> Impostazioni
          </Button>
          <Button data-testid="bday-run-now" onClick={() => runNow("birthday")} disabled={runningBday}
                  variant="outline" className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-11">
            <Cake className="h-4 w-4 mr-2" /> {runningBday ? "…" : "Compleanni oggi"}
          </Button>
          <Button data-testid="aniv-run-now" onClick={() => runNow("anniversary")} disabled={runningAniv}
                  variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 h-11">
            <Gift className="h-4 w-4 mr-2" /> {runningAniv ? "…" : "Anniversari oggi"}
          </Button>
          <Button data-testid="wback-run-now" onClick={() => runNow("winback")} disabled={runningWback}
                  variant="outline" className="border-rose-500/40 text-rose-300 hover:bg-rose-500/10 h-11">
            <UserMinus className="h-4 w-4 mr-2" /> {runningWback ? "…" : "Recupero inattivi"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <StatTile label="Totali" value={rowStats.total} accent="text-amber-300" icon={Sparkles} tid="stat-total" />
        <StatTile label="Compleanni" value={rowStats.bday} accent="text-rose-300" icon={Cake} tid="stat-bday" />
        <StatTile label="Anniversari" value={rowStats.aniv} accent="text-emerald-300" icon={Gift} tid="stat-aniv" />
        <StatTile label="Recupero" value={rowStats.wback} accent="text-fuchsia-300" icon={HeartHandshake} tid="stat-wback" />
        <StatTile label="Usati" value={rowStats.used} accent="text-sky-300" icon={Award} tid="stat-used" />
      </div>

      {/* Redemption stats mini chart */}
      <div className="card-elev p-4" data-testid="birthdays-stats-chart">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold flex items-center gap-1.5">
              <TrendingUp className="h-3 w-3" /> Tasso di riscatto
            </div>
            <div className="font-heading text-lg font-bold mt-0.5">
              {statsView === "kind" ? `Ultimi ${statsDays} giorni` : `Per sede · ultimi ${statsDays} giorni`}
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <div className="flex gap-1" data-testid="birthdays-stats-view">
              {[{k:"kind",label:"Per tipo"},{k:"branch",label:"Per sede"}].map((v) => (
                <button key={v.k} onClick={() => setStatsView(v.k)}
                        data-testid={`birthdays-stats-view-${v.k}`}
                        className={`px-3 h-8 rounded-full text-[11px] font-semibold border transition-all ${statsView === v.k
                          ? "bg-fuchsia-400 text-zinc-950 border-fuchsia-400"
                          : "bg-zinc-950 text-fuchsia-300 border-zinc-800 hover:border-fuchsia-500/60"}`}>
                  {v.label}
                </button>
              ))}
            </div>
            <div className="flex gap-1" data-testid="birthdays-stats-window">
              {[30, 90].map((d) => (
                <button key={d} onClick={() => setStatsDays(d)}
                        data-testid={`birthdays-stats-window-${d}`}
                        className={`px-3 h-8 rounded-full text-[11px] font-semibold border transition-all ${statsDays === d
                          ? "bg-amber-400 text-zinc-950 border-amber-400"
                          : "bg-zinc-950 text-amber-300 border-zinc-800 hover:border-amber-500/60"}`}>
                  {d}g
                </button>
              ))}
            </div>
          </div>
        </div>
        {statsView === "branch" ? (
          statsBranchRows.length === 0 ? (
            <div className="text-xs text-zinc-500 py-8 text-center">Nessun dato per sede nel periodo.</div>
          ) : (
            <div className="overflow-x-auto" data-testid="birthdays-branch-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                    <th className="text-left py-2 pr-3">Sede</th>
                    <th className="text-right py-2 pr-3">🎂 Compleanno</th>
                    <th className="text-right py-2 pr-3">🎁 Anniversario</th>
                    <th className="text-right py-2 pr-3">💌 Recupero</th>
                    <th className="text-right py-2">Riscatto totale</th>
                  </tr>
                </thead>
                <tbody>
                  {statsBranchRows.map((r) => (
                    <tr key={r.branch.id} className="border-b border-zinc-900" data-testid={`birthdays-branch-row-${r.branch.id}`}>
                      <td className="py-2.5 pr-3 font-semibold">{r.branch.name}</td>
                      <td className="py-2.5 pr-3 text-right font-mono text-rose-300">{r.birthday.used}/{r.birthday.issued}</td>
                      <td className="py-2.5 pr-3 text-right font-mono text-emerald-300">{r.anniversary.used}/{r.anniversary.issued}</td>
                      <td className="py-2.5 pr-3 text-right font-mono text-fuchsia-300">{r.winback.used}/{r.winback.issued}</td>
                      <td className="py-2.5 text-right">
                        <span className="font-heading font-extrabold text-amber-300">{Math.round(r.total_rate * 100)}%</span>
                        <span className="text-[10px] text-zinc-500 ml-1">({r.total_used}/{r.total_issued})</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : chartData.every((r) => r.issued === 0) ? (
          <div className="text-xs text-zinc-500 py-8 text-center">Nessun coupon automatico emesso nel periodo.</div>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-3">
              {chartData.map((c) => (
                <div key={c.key} className="rounded-lg border border-zinc-800 bg-zinc-950 p-3" data-testid={`birthdays-rate-${c.key}`}>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{c.name}</div>
                  <div className="mt-1 flex items-baseline gap-1">
                    <span className="font-heading text-2xl font-extrabold" style={{ color: c.color }}>{c.rate}%</span>
                    <span className="text-[11px] text-zinc-500">riscatto</span>
                  </div>
                  <div className="text-[11px] text-zinc-500">{c.used}/{c.issued} usati</div>
                </div>
              ))}
            </div>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={120}>
                <BarChart data={chartData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
                  <YAxis tick={{ fill: "#a1a1aa", fontSize: 11 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
                  <Tooltip contentStyle={{ background: "#18181b", border: "1px solid #3f3f46", borderRadius: 8, fontSize: 12 }}
                           labelStyle={{ color: "#fbbf24" }}
                           formatter={(v, n) => [v, n === "issued" ? "Emessi" : "Usati"]} />
                  <Bar dataKey="issued" fill="#3f3f46" name="Emessi" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, i) => <Cell key={i} fill={entry.color + "55"} />)}
                  </Bar>
                  <Bar dataKey="used" name="Usati" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}
      </div>

      <div className="card-elev p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex gap-2 flex-wrap" data-testid="birthdays-tabs">
            {KINDS.map((t) => {
              const Icon = t.icon;
              const active = kind === t.k;
              return (
                <button key={t.k} onClick={() => setKind(t.k)} data-testid={`birthdays-tab-${t.k}`}
                        className={`px-3 h-9 rounded-full text-sm font-semibold flex items-center gap-1.5 border transition-all ${active
                          ? "bg-amber-400 text-zinc-950 border-amber-400"
                          : "bg-zinc-950 text-amber-300 border-zinc-800 hover:border-amber-500/60"}`}>
                  <Icon className="h-3.5 w-3.5" /> {t.label}
                </button>
              );
            })}
          </div>
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca cliente, carta o codice…"
                   className="bg-zinc-950 border-zinc-800 pl-9 h-9" data-testid="birthdays-search" />
          </div>
          <Button variant="outline" size="sm" onClick={load} className="border-zinc-800 h-9" data-testid="birthdays-refresh">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-zinc-500 py-10 text-center">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-zinc-500 py-12 text-center" data-testid="birthdays-empty">
            Nessun coupon automatico nel periodo.
          </div>
        ) : (
          <div className="overflow-x-auto mobile-cards-wrap">
            <table className="w-full text-sm mobile-cards" data-testid="birthdays-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                  <th className="text-left py-2 pr-3">Cliente</th>
                  <th className="text-left py-2 pr-3">Tipo</th>
                  <th className="text-left py-2 pr-3">Codice</th>
                  <th className="text-right py-2 pr-3">Valore</th>
                  <th className="text-left py-2 pr-3">Stato</th>
                  <th className="text-left py-2 pr-3">Scadenza</th>
                  <th className="text-right py-2">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const meta = KIND_META[r.kind] || { label: r.kind, pill: "pill-muted" };
                  return (
                    <tr key={r.coupon_id + (r.customer?.id || i)} className="border-b border-zinc-900 hover:bg-zinc-950/60"
                        data-testid={`birthdays-row-${r.coupon_id}-${r.customer?.id || i}`}>
                      <td data-label="Cliente" className="py-2.5 pr-3">
                        <div className="font-medium">{r.customer?.name || "—"}</div>
                        <div className="text-[11px] text-zinc-500 font-mono">{r.customer?.card_number || "—"}</div>
                      </td>
                      <td data-label="Tipo" className="py-2.5 pr-3"><span className={`pill text-[10px] ${meta.pill}`}>{meta.label}</span></td>
                      <td data-label="Codice" className="py-2.5 pr-3 font-mono text-[11px] text-amber-300">{r.code}</td>
                      <td data-label="Valore" className="py-2.5 pr-3 text-right font-mono text-emerald-300">€ {Number(r.discount_value).toFixed(2)}</td>
                      <td data-label="Stato" className="py-2.5 pr-3">
                        <span className={`pill text-[10px] ${r.status === "used" ? "pill-muted" : "pill-gold"}`}>
                          {r.status === "used" ? "Usato" : "Disponibile"}
                        </span>
                      </td>
                      <td data-label="Scadenza" className="py-2.5 pr-3 text-zinc-400 text-[12px]">{(r.valid_until || "").slice(0, 10) || "—"}</td>
                      <td data-label="Azioni" className="py-2.5 text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => setPreviewRow(r)}
                                  className="border-zinc-800 hover:border-sky-500 hover:text-sky-300 h-8"
                                  data-testid={`birthdays-preview-${r.coupon_id}`} title="Preview Wallet">
                            <Wallet className="h-3 w-3" />
                          </Button>
                          {r.customer && (
                            <Button size="sm" variant="outline" onClick={() => reissue(r)}
                                    disabled={reissuingId === (r.customer.id + r.kind)}
                                    className="border-zinc-800 hover:border-amber-500 hover:text-amber-300 h-8"
                                    data-testid={`birthdays-reissue-${r.customer.id}-${r.kind}`} title="Riemetti il coupon">
                              <Send className="h-3 w-3 mr-1" /> Riemetti
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <WalletPreviewDialog row={previewRow} onClose={() => setPreviewRow(null)} />
      <AutoCouponConfigDialog open={cfgOpen} onClose={() => setCfgOpen(false)} onSaved={() => { loadStats(); }} />
    </div>
  );
}

function StatTile({ label, value, accent, icon: Icon, tid }) {
  return (
    <div className="card-elev p-3.5" data-testid={tid}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-1.5">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className={`font-mono font-extrabold text-2xl mt-1 ${accent}`}>{value}</div>
    </div>
  );
}

function AutoCouponConfigDialog({ open, onClose, onSaved }) {
  const [tab, setTab] = useState("birthday");
  const [bday, setBday] = useState(null);
  const [wback, setWback] = useState(null);
  const [branches, setBranches] = useState([]);
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewDate, setPreviewDate] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [b, w, br] = await Promise.all([
          api.get("/settings/birthday-coupon").then(r => r.data),
          api.get("/settings/winback-coupon").then(r => r.data),
          api.get("/branches").then(r => r.data).catch(() => []),
        ]);
        setBday({ ...b, branch_ids: b.branch_ids || [] });
        setWback({ ...w, branch_ids: w.branch_ids || [] });
        setBranches(br);
      } catch (err) { toast.error(formatApiError(err)); }
    })();
  }, [open]);

  const save = async () => {
    setSaving(true);
    try {
      if (tab === "birthday") {
        await api.put("/settings/birthday-coupon", bday);
        toast.success("Compleanni & anniversari salvato");
      } else {
        await api.put("/settings/winback-coupon", wback);
        toast.success("Recupero salvato");
      }
      onSaved?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const runPreview = async () => {
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const kinds = tab === "birthday" ? ["birthday", "anniversary"] : ["winback"];
      const results = await Promise.all(kinds.map((k) =>
        api.get(`/segments/${k}/preview`, { params: previewDate ? { simulate_date: previewDate } : {} })
           .then((r) => ({ kind: k, ...r.data }))
      ));
      setPreviewRows(results);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setPreviewLoading(false); }
  };

  const cur = tab === "birthday" ? bday : wback;
  const setCur = tab === "birthday" ? setBday : setWback;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md max-h-[90vh] overflow-y-auto" data-testid="birthdays-config-dialog">
        <DialogHeader><DialogTitle>Impostazioni coupon automatici</DialogTitle></DialogHeader>
        <div className="flex gap-2 mb-2">
          <button onClick={() => setTab("birthday")} data-testid="birthdays-config-tab-birthday"
                  className={`px-3 h-9 rounded-full text-sm font-semibold border transition-all ${tab === "birthday"
                    ? "bg-amber-400 text-zinc-950 border-amber-400"
                    : "bg-zinc-950 text-amber-300 border-zinc-800 hover:border-amber-500/60"}`}>
            🎂 Compleanni & Anniv.
          </button>
          <button onClick={() => setTab("winback")} data-testid="birthdays-config-tab-winback"
                  className={`px-3 h-9 rounded-full text-sm font-semibold border transition-all ${tab === "winback"
                    ? "bg-fuchsia-400 text-zinc-950 border-fuchsia-400"
                    : "bg-zinc-950 text-fuchsia-300 border-zinc-800 hover:border-fuchsia-500/60"}`}>
            💌 Recupero
          </button>
        </div>
        {!cur ? (
          <div className="py-6 text-sm text-zinc-500 text-center">Caricamento…</div>
        ) : (
          <div className="space-y-3">
            <p className="text-[11px] text-zinc-500">
              Importo coupon = <span className="font-mono text-amber-300">base + (anni_iscrizione × step)</span>, con tetto massimo <span className="font-mono text-amber-300">cap</span>.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-zinc-300 text-[11px] mb-1 inline-block">Importo base (€)</Label>
                <Input type="number" step="0.5" min="0" value={cur.base_eur}
                       onChange={(e) => setCur({ ...cur, base_eur: parseFloat(e.target.value || 0) })}
                       className="bg-zinc-950 border-zinc-800 font-mono" data-testid={`bcfg-${tab}-base`} />
              </div>
              <div>
                <Label className="text-zinc-300 text-[11px] mb-1 inline-block">Step per anno (€)</Label>
                <Input type="number" step="0.5" min="0" value={cur.step_per_year}
                       onChange={(e) => setCur({ ...cur, step_per_year: parseFloat(e.target.value || 0) })}
                       className="bg-zinc-950 border-zinc-800 font-mono" data-testid={`bcfg-${tab}-step`} />
              </div>
              <div>
                <Label className="text-zinc-300 text-[11px] mb-1 inline-block">Tetto max (€)</Label>
                <Input type="number" step="1" min="0" value={cur.cap_eur}
                       onChange={(e) => setCur({ ...cur, cap_eur: parseFloat(e.target.value || 0) })}
                       className="bg-zinc-950 border-zinc-800 font-mono" data-testid={`bcfg-${tab}-cap`} />
              </div>
              <div>
                <Label className="text-zinc-300 text-[11px] mb-1 inline-block">Spesa minima (€)</Label>
                <Input type="number" step="0.5" min="0" value={cur.min_spend_eur}
                       onChange={(e) => setCur({ ...cur, min_spend_eur: parseFloat(e.target.value || 0) })}
                       className="bg-zinc-950 border-zinc-800 font-mono" data-testid={`bcfg-${tab}-minspend`} />
              </div>
              <div>
                <Label className="text-zinc-300 text-[11px] mb-1 inline-block">Validità (giorni)</Label>
                <Input type="number" step="1" min="1" value={cur.validity_days}
                       onChange={(e) => setCur({ ...cur, validity_days: parseInt(e.target.value || 0) || 1 })}
                       className="bg-zinc-950 border-zinc-800 font-mono" data-testid={`bcfg-${tab}-days`} />
              </div>
              {tab === "winback" && (
                <div>
                  <Label className="text-zinc-300 text-[11px] mb-1 inline-block">Inattività (giorni)</Label>
                  <Input type="number" step="1" min="1" value={cur.inactive_days || 60}
                         onChange={(e) => setCur({ ...cur, inactive_days: parseInt(e.target.value || 0) || 60 })}
                         className="bg-zinc-950 border-zinc-800 font-mono" data-testid={`bcfg-${tab}-inactive`} />
                </div>
              )}
            </div>
            <div>
              <Label className="text-zinc-300 text-[11px] mb-1 inline-block">Sedi in cui è spendibile (vuoto = tutte)</Label>
              <BranchMultiSelect
                branches={branches}
                value={cur.branch_ids || []}
                onChange={(ids) => setCur({ ...cur, branch_ids: ids })}
                testId={`bcfg-${tab}-branches`}
              />
            </div>
            <div className="text-[11px] text-zinc-500 rounded-lg border border-amber-500/25 bg-amber-500/5 p-2.5">
              Esempio: cliente con {tab === "winback" ? "3 anni" : "2 anni"} di iscrizione → coupon di <span className="font-mono text-amber-300">
                € {Math.min(cur.cap_eur, cur.base_eur + (tab === "winback" ? 3 : 2) * cur.step_per_year).toFixed(2)}
              </span>, valido {cur.validity_days} gg, spesa min € {Number(cur.min_spend_eur).toFixed(2)}.
            </div>
            <Button data-testid={`bcfg-${tab}-save`} onClick={save} disabled={saving}
                    className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]">
              {saving ? "Salvataggio…" : "Salva"}
            </Button>
            <div className="rounded-lg border border-sky-500/30 bg-sky-500/5 p-3 space-y-2" data-testid={`bcfg-${tab}-preview-panel`}>
              <div className="flex items-center gap-1.5">
                <Search className="h-3 w-3 text-sky-300" />
                <span className="text-[11px] uppercase tracking-widest text-sky-300 font-semibold">Test cron</span>
              </div>
              <div className="flex gap-2">
                <Input type="date" value={previewDate} onChange={(e) => setPreviewDate(e.target.value)}
                       placeholder="Data (default: domani)"
                       className="bg-zinc-950 border-zinc-800 h-9 text-sm flex-1"
                       data-testid={`bcfg-${tab}-preview-date`} />
                <Button onClick={runPreview} disabled={previewLoading} variant="outline"
                        className="border-sky-500/40 text-sky-300 hover:bg-sky-500/10 h-9"
                        data-testid={`bcfg-${tab}-preview-btn`}>
                  {previewLoading ? "…" : "Simula"}
                </Button>
              </div>
              <p className="text-[10px] text-zinc-500">Anteprima dei clienti che riceverebbero il coupon (senza inviare nulla).</p>
            </div>
          </div>
        )}
      </DialogContent>
      {/* Preview results dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="birthdays-preview-dialog">
          <DialogHeader><DialogTitle>Test cron · anteprima invii</DialogTitle></DialogHeader>
          {previewLoading ? (
            <div className="py-8 text-center text-sm text-zinc-500">Simulazione…</div>
          ) : previewRows.length === 0 ? (
            <div className="py-8 text-center text-sm text-zinc-500">Nessun cliente coinvolto per la data scelta.</div>
          ) : (
            <div className="space-y-4">
              {previewRows.map((section) => (
                <div key={section.kind}>
                  <div className="text-[11px] uppercase tracking-widest text-amber-300 font-semibold mb-2">
                    {section.kind === "birthday" ? "🎂 Compleanno" : section.kind === "anniversary" ? "🎁 Anniversario" : "💌 Recupero"} — data simulata {section.simulate_date} · <span className="text-zinc-400">{section.matched} clienti</span>
                  </div>
                  {section.customers?.length === 0 ? (
                    <div className="text-[11px] text-zinc-500 pl-2">Nessun cliente coinvolto</div>
                  ) : (
                    <div className="space-y-1">
                      {section.customers.map((cust) => (
                        <div key={cust.customer_id} className="rounded-lg border border-zinc-800 bg-zinc-950 p-2.5 flex items-center justify-between gap-2"
                             data-testid={`birthdays-preview-row-${cust.customer_id}-${section.kind}`}>
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-sm truncate">{cust.name}</div>
                            <div className="text-[11px] text-zinc-500 font-mono">{cust.card_number} · {cust.tenure_years} anni</div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-heading font-extrabold text-amber-300">€ {Number(cust.would_receive.discount_value).toFixed(2)}</div>
                            <div className="text-[10px] text-zinc-500 font-mono">{cust.would_receive.code}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}

// Simulacrum of Apple/Google Wallet card previewing the coupon.
function WalletPreviewDialog({ row, onClose }) {
  const { settings } = useAppSettings();
  const [platform, setPlatform] = useState("apple");
  useEffect(() => { if (row) setPlatform("apple"); }, [row?.coupon_id]);
  if (!row) return null;
  const appName = settings?.app_name || "Loyalty Club";
  const logoUrl = settings?.logo_url ? resolveImageUrl(settings.logo_url) : null;
  const custName = row.customer?.name || "Cliente";
  const cardNum = row.customer?.card_number || "";
  const validUntil = (row.valid_until || "").slice(0, 10);
  const meta = KIND_META[row.kind] || { label: "Coupon", pill: "pill-gold" };
  const barcodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=520x160&margin=2&bgcolor=ffffff&color=18181c&format=svg&data=${encodeURIComponent(row.code)}`;
  const pdf417Url = `https://barcodeapi.org/api/pdf417/${encodeURIComponent(row.code)}`;
  return (
    <Dialog open={!!row} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800 max-w-md p-0 overflow-hidden max-h-[92vh] overflow-y-auto"
                     data-testid="wallet-preview-dialog">
        <DialogHeader className="px-5 pt-5">
          <DialogTitle>Preview Wallet · {meta.label}</DialogTitle>
        </DialogHeader>
        <div className="px-5 pb-2 flex gap-2" data-testid="wallet-preview-tabs">
          <button type="button" onClick={() => setPlatform("apple")} data-testid="wallet-preview-apple-btn"
            className={`px-3 h-8 rounded-full text-[11px] font-semibold border transition-all ${platform === "apple"
              ? "bg-zinc-100 text-zinc-950 border-zinc-100"
              : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-zinc-500"}`}>Apple Wallet</button>
          <button type="button" onClick={() => setPlatform("google")} data-testid="wallet-preview-google-btn"
            className={`px-3 h-8 rounded-full text-[11px] font-semibold border transition-all ${platform === "google"
              ? "bg-emerald-400 text-emerald-950 border-emerald-400"
              : "bg-zinc-950 text-zinc-300 border-zinc-800 hover:border-zinc-500"}`}>Google Wallet</button>
        </div>
        <div className="p-5 pt-3">
          {platform === "apple" ? (
            <div data-testid="wallet-preview-apple"
                 className="rounded-2xl bg-gradient-to-b from-zinc-800 to-black text-zinc-100 p-4 shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-zinc-800/80">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {logoUrl ? <img src={logoUrl} alt="logo" className="h-6 w-6 rounded object-cover" /> : <Wallet className="h-5 w-5" />}
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">{appName}</div>
                </div>
                <div className="text-[10px] text-zinc-500">COUPON</div>
              </div>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-zinc-500">Sconto</div>
              <div className="font-heading text-4xl font-extrabold text-amber-300">€ {Number(row.discount_value).toFixed(2)}</div>
              <div className="mt-3 grid grid-cols-2 gap-3">
                <div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Cliente</div><div className="text-sm font-semibold truncate">{custName}</div></div>
                <div className="text-right"><div className="text-[9px] uppercase tracking-widest text-zinc-500">Valido fino</div><div className="text-sm font-semibold">{validUntil}</div></div>
                <div><div className="text-[9px] uppercase tracking-widest text-zinc-500">Spesa minima</div><div className="text-sm">€ {Number(row.min_spend || 0).toFixed(2)}</div></div>
                <div className="text-right"><div className="text-[9px] uppercase tracking-widest text-zinc-500">Carta</div><div className="text-sm font-mono">{cardNum}</div></div>
              </div>
              <div className="mt-4 rounded bg-white p-2 flex items-center justify-center">
                <img src={pdf417Url} alt="PDF417" className="w-full h-14 object-contain" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              </div>
              <div className="text-center text-[10px] font-mono text-zinc-950 bg-white pb-2 -mt-2">{row.code}</div>
              <div className="text-[10px] text-zinc-500 mt-3 text-center">Aggiungi ad Apple Wallet → mostra questo barcode in cassa</div>
            </div>
          ) : (
            <div data-testid="wallet-preview-google"
                 className="rounded-2xl overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.5)] border border-emerald-500/30">
              <div className="bg-gradient-to-br from-emerald-500 via-emerald-600 to-emerald-800 text-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {logoUrl ? <img src={logoUrl} alt="logo" className="h-7 w-7 rounded-full bg-white p-0.5" /> : <Wallet className="h-5 w-5" />}
                    <div>
                      <div className="text-[10px] uppercase tracking-widest opacity-80">{appName}</div>
                      <div className="font-heading text-sm font-bold">{meta.label}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] uppercase tracking-widest opacity-80">Sconto</div>
                    <div className="font-heading text-2xl font-extrabold">€ {Number(row.discount_value).toFixed(2)}</div>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  <div><div className="text-[9px] uppercase tracking-widest opacity-80">Cliente</div><div className="text-sm font-semibold truncate">{custName}</div></div>
                  <div className="text-right"><div className="text-[9px] uppercase tracking-widest opacity-80">Scadenza</div><div className="text-sm font-semibold">{validUntil}</div></div>
                </div>
              </div>
              <div className="bg-white p-4">
                <img src={barcodeUrl} alt="QR" className="w-full h-32 object-contain" />
                <div className="text-center text-[11px] font-mono text-zinc-900 mt-2">{row.code}</div>
              </div>
              <div className="bg-zinc-950 text-zinc-400 text-[10px] p-3 text-center border-t border-zinc-800">
                Salva in Google Wallet · Scadenza {validUntil} · Spesa min € {Number(row.min_spend || 0).toFixed(2)}
              </div>
            </div>
          )}
          <p className="text-[10px] text-zinc-500 mt-3 text-center">
            Anteprima grafica indicativa · nel Wallet reale il barcode viene renderizzato nativamente.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
